import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Music, CheckCircle, XCircle, Loader2, Eye, Trash2, 
  Star, StarOff, Search, RefreshCw, ExternalLink, Plus, Edit
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../hooks/useAdmin';

import { API_URL } from '../../utils/api';

export function AdminArtistsTab() {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('pending'); // 'all', 'pending', 'approved'
  const [processing, setProcessing] = useState(null);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/artists/admin/all`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setArtists(data);
      } else {
        toast.error('Failed to load artists');
      }
    } catch (error) {
      toast.error('Error loading artists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const approveArtist = async (artistId) => {
    setProcessing(artistId);
    try {
      const response = await fetch(`${API_URL}/api/artists/admin/${artistId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Artist approved');
        setArtists(prev => prev.map(a => 
          a.artist_id === artistId ? { ...a, is_approved: true } : a
        ));
      } else {
        toast.error('Failed to approve artist');
      }
    } catch (error) {
      toast.error('Error approving artist');
    } finally {
      setProcessing(null);
    }
  };

  const toggleFeatured = async (artistId, currentFeatured) => {
    setProcessing(artistId);
    try {
      const response = await fetch(`${API_URL}/api/artists/admin/${artistId}/feature`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_featured: !currentFeatured })
      });
      if (response.ok) {
        toast.success(currentFeatured ? 'Removed from featured' : 'Added to featured');
        setArtists(prev => prev.map(a => 
          a.artist_id === artistId ? { ...a, is_featured: !currentFeatured } : a
        ));
      } else {
        toast.error('Failed to update featured status');
      }
    } catch (error) {
      toast.error('Error updating featured status');
    } finally {
      setProcessing(null);
    }
  };

  const deleteArtist = async (artistId, artistName) => {
    if (!window.confirm(`Are you sure you want to delete "${artistName}"?`)) return;
    
    setProcessing(artistId);
    try {
      const response = await fetch(`${API_URL}/api/artists/${artistId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Artist deleted');
        setArtists(prev => prev.filter(a => a.artist_id !== artistId));
      } else {
        toast.error('Failed to delete artist');
      }
    } catch (error) {
      toast.error('Error deleting artist');
    } finally {
      setProcessing(null);
    }
  };

  const filteredArtists = artists.filter(artist => {
    // Filter by approval status
    if (filter === 'pending' && artist.is_approved) return false;
    if (filter === 'approved' && !artist.is_approved) return false;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        artist.name?.toLowerCase().includes(query) ||
        artist.stage_name?.toLowerCase().includes(query) ||
        artist.location?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const pendingCount = artists.filter(a => !a.is_approved).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Artists Management</h2>
          <p className="text-sm text-slate-500">
            {pendingCount > 0 ? (
              <span className="text-orange-600 font-medium">{pendingCount} pending approval</span>
            ) : (
              'All artists approved'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate('/admin/add-artist')} 
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="add-artist-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Artist
          </Button>
          <Button onClick={fetchArtists} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('pending')}
          >
            Pending {pendingCount > 0 && `(${pendingCount})`}
          </Button>
          <Button
            variant={filter === 'approved' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('approved')}
          >
            Approved
          </Button>
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </Button>
        </div>
      </div>

      {/* Artists List */}
      {filteredArtists.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Music className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {filter === 'pending' ? 'No pending artists' : 'No artists found'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredArtists.map((artist) => (
            <div
              key={artist.artist_id}
              className={`bg-white rounded-xl border p-4 ${
                !artist.is_approved ? 'border-orange-200 bg-orange-50/50' : 'border-slate-200'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                {artist.image_url ? (
                  <img
                    src={artist.image_url}
                    alt={artist.name}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Music className="w-8 h-8 text-white" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900">
                      {artist.stage_name || artist.name}
                    </h3>
                    {artist.is_featured && (
                      <Badge className="bg-yellow-100 text-yellow-700">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {!artist.is_approved && (
                      <Badge className="bg-orange-100 text-orange-700">
                        Pending Approval
                      </Badge>
                    )}
                  </div>
                  {artist.stage_name && artist.name !== artist.stage_name && (
                    <p className="text-sm text-slate-500">{artist.name}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    {artist.location && <span>{artist.location}</span>}
                    {artist.performance_types?.length > 0 && (
                      <span>{artist.performance_types.join(', ')}</span>
                    )}
                  </div>
                  {artist.bio && (
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{artist.bio}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2">
                  {!artist.is_approved && (
                    <Button
                      size="sm"
                      onClick={() => approveArtist(artist.artist_id)}
                      disabled={processing === artist.artist_id}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {processing === artist.artist_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </>
                      )}
                    </Button>
                  )}
                  
                  {artist.is_approved && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleFeatured(artist.artist_id, artist.is_featured)}
                      disabled={processing === artist.artist_id}
                    >
                      {artist.is_featured ? (
                        <>
                          <StarOff className="w-4 h-4 mr-1" />
                          Unfeature
                        </>
                      ) : (
                        <>
                          <Star className="w-4 h-4 mr-1" />
                          Feature
                        </>
                      )}
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin/edit-artist/${artist.artist_id}`)}
                    title="Edit artist"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/artists/${artist.slug}`, '_blank')}
                    title="View artist page"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteArtist(artist.artist_id, artist.name)}
                    disabled={processing === artist.artist_id}
                    className="text-red-600 hover:bg-red-50"
                    title="Delete artist"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
